import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, of } from 'rxjs';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { CrmClient } from '../../../crm/crm.model';
import { CrmService } from '../../../crm/crm.service';
import { LogisticsProjectSummary } from '../../../logistics-projects/models/logistics-project.model';
import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { LogisticsProjectsService } from '../../../logistics-projects/logistics-projects.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Driver } from '../../../fleet/driver/driver.model';
import { DriverService } from '../../../fleet/driver/driver.service';
import { Vehicle } from '../../../fleet/vehicle/vehicle.model';
import { VehicleService } from '../../../fleet/vehicle/vehicle.service';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { resolveSisterCompanyScope, sisterCompanyFleetAccessMessage } from '../../../shared/utils/sister-company-scope.util';
import { BulkCost, BulkCostAccountDefault, BulkCostLine } from './bulk-cost.model';
import { BulkCostService } from './bulk-cost.service';

type PartyMode = 'SUPPLIER' | 'OWN_COMPANY' | 'SISTER_COMPANY';

@Component({
  selector: 'app-bulk-cost-form',
  standalone: true,
  imports: [
        LogisticsProjectLookupComponent,
    CommonModule,
    FormsModule,
    ToastModule,
    ProgressBarModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TagModule,
    TextareaModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './bulk-cost-form.component.html',
  styleUrls: ['../../finance-list.shared.css', './bulk-cost-form.component.css']
})
export class BulkCostFormComponent implements OnInit {
  bulkCost: BulkCost = { currencyCode: 'SAR', exchangeRate: 1, lines: [] };
  partyMode: PartyMode = 'SUPPLIER';
  selectedCrmClient?: CrmClient | null;
  selectedSisterCompanyId?: number | null;
  crmClientOptions: SelectItem<CrmClient>[] = [];
  sisterCompanyOptions: SelectItem<number>[] = [];
  driverOptions: SelectItem<number>[] = [];
  vehicleOptions: SelectItem<number>[] = [];
  private readonly accountDefaultsBySource = new Map<string, BulkCostAccountDefault>();
  private readonly driversById = new Map<number, Driver>();
  private readonly vehiclesById = new Map<number, Vehicle>();
  accounts: ChartOfAccounts[] = [];
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  sourceTypeOptions = [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Shipment Internal Cost', value: 'SHIPMENT_INTERNAL_COST' },
    { label: 'Shipment', value: 'SHIPMENT' },
    { label: 'Delivery', value: 'DELIVERY' },
    { label: 'Sales Order', value: 'SALES_ORDER' },
    { label: 'Order Release', value: 'ORDER_RELEASE' },
    { label: 'Clearance', value: 'CLEARANCE' },
    { label: 'Warehousing', value: 'WAREHOUSING' },
    { label: 'Project', value: 'PROJECT' }
  ];
  readonly partyTypeOptions: SelectItem<PartyMode>[] = [
    { label: 'CRM Supplier', value: 'SUPPLIER' },
    { label: 'Own Company', value: 'OWN_COMPANY' },
    { label: 'Sister Company', value: 'SISTER_COMPANY' }
  ];
  readonly allocationModeOptions: SelectItem<string>[] = [
    { label: 'Direct line amounts', value: 'DIRECT' },
    { label: 'Split total by percentage', value: 'PERCENTAGE' }
  ];

  get pageTitle(): string {
    return this.bulkCost.bulkCostNumber ? `Bulk Cost ${this.bulkCost.bulkCostNumber}` : 'New Bulk Cost';
  }

  get subtitle(): string {
    return 'Review supplier or sister-company operational costs before approval and posting.';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly service: BulkCostService,
    private readonly session: SessionStorageService,
    private readonly messageService: MessageService,
    private readonly chartOfAccountsService: ChartOfAccountsService,
    private readonly crmService: CrmService,
    private readonly logisticsProjectsService: LogisticsProjectsService,
    private readonly tenantScopeService: TenantScopeService,
    private readonly driverService: DriverService,
    private readonly vehicleService: VehicleService
  ) {}

  ngOnInit(): void {
    this.bulkCost = this.newBulkCost();
    this.loadLookups();
    const id = Number(this.route.snapshot.paramMap.get('id') || 0);
    if (id) {
      this.isEditMode = true;
      this.loadBulkCost(id);
    }
  }

  onPartyModeChange(mode: PartyMode): void {
    this.partyMode = mode;
    this.selectedCrmClient = null;
    this.selectedSisterCompanyId = null;
    this.bulkCost.supplierPartyId = undefined;
    this.bulkCost.supplierPartyType = undefined;
    this.bulkCost.supplierName = undefined;
    if (mode === 'OWN_COMPANY') {
      this.applyCompanyParty(this.companyId(), this.session.getCompany()?.name);
    }
  }

  onCrmClientChange(client: CrmClient | null): void {
    this.selectedCrmClient = client;
    if (!client) {
      this.bulkCost.supplierPartyId = undefined;
      this.bulkCost.supplierPartyType = undefined;
      this.bulkCost.supplierName = undefined;
      return;
    }
    this.bulkCost.supplierPartyId = client.linkedCustomerId || client.linkedCompanyId || client.id;
    this.bulkCost.supplierPartyType = client.linkedCustomerId ? 'Customer' : 'Company';
    this.bulkCost.supplierName = client.companyName || client.notifyingPersonName || client.clientNumber || 'CRM Supplier';
  }

  onSisterCompanyChange(companyId: number | null): void {
    this.selectedSisterCompanyId = companyId;
    const company = this.sisterCompanyOptions.find(option => option.value === companyId);
    this.bulkCost.supplierPartyId = companyId || undefined;
    this.bulkCost.supplierPartyType = companyId ? 'Company' : undefined;
    this.bulkCost.supplierName = company?.label || undefined;
  }

  addLine(): void {
    this.bulkCost.lines = [...(this.bulkCost.lines || []), this.newLine()];
    if (this.isPercentageAllocation) {
      this.distributeEvenPercentages();
    }
  }

  onSourceTypeChange(sourceType: string): void {
    this.bulkCost.sourceType = sourceType;
    (this.bulkCost.lines || []).forEach(line => line.sourceType = sourceType);
    this.applyAccountDefaults(sourceType);
  }

  removeLine(index: number): void {
    if ((this.bulkCost.lines || []).length <= 1) {
      this.bulkCost.lines = [this.newLine()];
      return;
    }
    this.bulkCost.lines = (this.bulkCost.lines || []).filter((_, rowIndex) => rowIndex !== index);
    if (this.isPercentageAllocation) {
      this.distributeEvenPercentages();
    }
  }

  duplicateLine(line: BulkCostLine): void {
    this.bulkCost.lines = [...(this.bulkCost.lines || []), { ...line, bulkCostLineId: undefined }];
    if (this.isPercentageAllocation) {
      this.distributeEvenPercentages();
    }
  }

  onProjectChange(line: BulkCostLine, project: LogisticsProjectSummary | null): void {
    line.jobReference = project ? this.projectLabel(project) : line.jobReference;
  }

  onDriverChange(line: BulkCostLine, driverId?: number | null): void {
    line.driverId = driverId || undefined;
    line.driverName = this.driverOptions.find(option => option.value === driverId)?.label;
    this.syncPartyFromAssignedAssets();
  }

  onVehicleChange(line: BulkCostLine, vehicleId?: number | null): void {
    line.vehicleId = vehicleId || undefined;
    line.vehicleLabel = this.vehicleOptions.find(option => option.value === vehicleId)?.label;
    this.syncPartyFromAssignedAssets();
  }

  get isPercentageAllocation(): boolean {
    return this.bulkCost.allocationMode === 'PERCENTAGE';
  }

  allocationPercentageTotal(): number {
    return (this.bulkCost.lines || []).reduce((total, line) => total + Number(line.allocationPercentage || 0), 0);
  }

  allocationIsBalanced(): boolean {
    return Math.abs(this.allocationPercentageTotal() - 100) < 0.00001;
  }

  onAllocationModeChange(mode: string): void {
    this.bulkCost.allocationMode = mode === 'PERCENTAGE' ? 'PERCENTAGE' : 'DIRECT';
    if (!this.isPercentageAllocation) {
      return;
    }
    this.bulkCost.allocationTotal = this.totalDirectAmount();
    this.distributeEvenPercentages();
  }

  save(): void {
    if (!this.bulkCost.owningCompanyId) {
      this.bulkCost.owningCompanyId = this.companyId();
    }
    if (!this.bulkCost.supplierPartyId || !this.bulkCost.supplierName) {
      this.messageService.add({ severity: 'warn', summary: 'Bulk cost', detail: 'Select a supplier CRM client or sister company.' });
      return;
    }
    if (this.isPercentageAllocation && !this.allocationIsBalanced()) {
      this.messageService.add({ severity: 'warn', summary: 'Bulk cost', detail: 'Percentage allocations must total exactly 100%.' });
      return;
    }
    if (this.isPercentageAllocation && Number(this.bulkCost.allocationTotal || 0) <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Bulk cost', detail: 'Enter a total amount greater than zero for percentage allocation.' });
      return;
    }
    const lines = (this.bulkCost.lines || []).filter((line, index) => this.lineAmount(line, index) > 0 || !!line.logisticsProjectId || !!line.jobReference);
    if (!lines.length) {
      this.messageService.add({ severity: 'warn', summary: 'Bulk cost', detail: 'Add at least one cost line.' });
      return;
    }
    this.bulkCost.lines = lines;
    this.bulkCost.grossAmount = this.totalAmount();
    this.bulkCost.taxAmount = this.totalTax();
    this.bulkCost.localAmount = this.totalLocal();
    this.isSaving = true;
    this.service.save(this.serialize(this.bulkCost)).subscribe({
      next: saved => {
        this.isSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Bulk cost', detail: `${saved.bulkCostNumber || 'Bulk cost'} saved.` });
        this.router.navigate(saved.bulkCostId
          ? ['/accounts/enterprise/payables/bulk-costs', saved.bulkCostId]
          : ['/accounts/enterprise/payables/bulk-costs']);
      },
      error: err => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save bulk cost.' });
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs']);
  }

  lineAmount(line: BulkCostLine, index = 0): number {
    if (this.isPercentageAllocation) {
      return this.percentageLineAmount(index);
    }
    return Number(line.quantity || 0) * Number(line.price || 0);
  }

  lineTotal(line: BulkCostLine, index = 0): number {
    return this.lineAmount(line, index) + (this.bulkCost.includeTax ? Number(line.taxAmount || 0) : 0);
  }

  totalAmount(): number {
    return this.isPercentageAllocation
      ? Number(this.bulkCost.allocationTotal || 0)
      : this.totalDirectAmount();
  }

  totalTax(): number {
    return (this.bulkCost.lines || []).reduce((sum, line) => sum + Number(line.taxAmount || 0), 0);
  }

  totalLocal(): number {
    const exchange = Number(this.bulkCost.exchangeRate || 1);
    return (this.bulkCost.lines || []).reduce((sum, line, index) => sum + this.lineTotal(line, index), 0) * exchange;
  }

  formatMoney(value?: number, currency = this.bulkCost.currencyCode || 'SAR'): string {
    return `${currency || ''} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  }

  accountLabel(account: ChartOfAccounts): string {
    return [account.accountCode, account.accountName].filter(Boolean).join(' - ');
  }

  private loadBulkCost(id: number): void {
    this.isLoading = true;
    this.service.get(id).subscribe({
      next: cost => {
        this.bulkCost = this.prepareForEdit(cost);
        this.resolveExistingParty();
        this.ensureSnapshotAssetOptions();
        this.syncPartyFromAssignedAssets();
        this.isLoading = false;
      },
      error: err => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Bulk cost', detail: err?.error?.message || 'Could not load bulk cost.' });
      }
    });
  }

  private loadLookups(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).subscribe(accounts => {
      this.accounts = accounts || [];
    });
    this.service.getAccountDefaults(companyId).subscribe({
      next: defaults => {
        this.accountDefaultsBySource.clear();
        (defaults || []).forEach(row => this.accountDefaultsBySource.set(row.sourceType, row));
        if (!this.isEditMode) {
          this.applyAccountDefaults(this.bulkCost.sourceType || 'MANUAL');
        }
      },
      error: () => undefined
    });
    this.crmService.getClients(companyId, ['%supplier%', '%vendor%']).subscribe(clients => {
      this.crmClientOptions = (clients || []).map(client => ({
        value: client,
        label: `${client.companyName || client.notifyingPersonName || 'CRM Supplier'}${client.clientNumber ? ` (${client.clientNumber})` : ''}`,
        disabled: !client.linkedCustomerId && !client.linkedCompanyId
      }));
      this.resolveExistingParty();
    });
    this.loadAssetOptions(companyId);
    this.initializeSisterCompanies();
  }

  private loadAssetOptions(companyId: number): void {
    const transportationAgencyId = this.session.getCompany()?.transportationAgencyId;
    const emptyDrivers = of({ items: [] as Driver[] });
    const emptyVehicles = of({ items: [] as Vehicle[] });

    forkJoin({
      drivers: forkJoin([
        transportationAgencyId
          ? this.driverService.findDriversByTransportationAgencyId(0, 500, transportationAgencyId)
          : emptyDrivers,
        this.driverService.getFreelanceDriversByCompanyId(0, 500, companyId),
        this.driverService.getRentalDriversByCompanyId(0, 500, companyId)
      ]),
      vehicles: forkJoin([
        transportationAgencyId
          ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 500, transportationAgencyId)
          : emptyVehicles,
        this.vehicleService.getFreelanceVehiclesByCompanyId(0, 500, companyId),
        this.vehicleService.getRentalVehiclesByCompanyId(0, 500, companyId)
      ])
    }).subscribe({
      next: result => {
        this.mergeAssetOptions(
          result.drivers.flatMap(response => response.items || []),
          result.vehicles.flatMap(response => response.items || [])
        );
      },
      error: () => this.ensureSnapshotAssetOptions()
    });
  }

  private initializeSisterCompanies(): void {
    const apply = () => {
      const scope = resolveSisterCompanyScope(this.tenantScopeService.getAccessSummary(), this.companyId());
      this.sisterCompanyOptions = (scope?.sisterCompanies || []).map(company => ({
        value: company.id,
        label: company.name || `Company #${company.id}`
      }));
      this.resolveExistingParty();
      this.syncPartyFromAssignedAssets();
      if (scope) {
        this.loadSisterOwnedAssetOptions(scope.companyGroupId);
      }
    };
    if (this.tenantScopeService.getAccessSummary()) {
      apply();
      return;
    }
    this.tenantScopeService.initializeCurrentAccess().subscribe(() => apply());
  }

  private resolveExistingParty(): void {
    const partyId = this.bulkCost.supplierPartyId;
    if (!partyId) return;
    const sisterMatch = this.sisterCompanyOptions.find(option => option.value === partyId);
    if (this.bulkCost.supplierPartyType === 'Company' && partyId === this.companyId()) {
      this.partyMode = 'OWN_COMPANY';
      return;
    }
    if (sisterMatch && this.bulkCost.supplierPartyType === 'Company') {
      this.partyMode = 'SISTER_COMPANY';
      this.selectedSisterCompanyId = partyId;
      return;
    }
    const clientMatch = this.crmClientOptions.find(option =>
      option.value?.linkedCustomerId === partyId || option.value?.linkedCompanyId === partyId || option.value?.id === partyId
    );
    if (clientMatch) {
      this.partyMode = 'SUPPLIER';
      this.selectedCrmClient = clientMatch.value;
    }
  }

  projectContextLabel(fallbackForMultiple = 'Multiple projects on cost lines'): string {
    if (this.bulkCost?.logisticsProjectNumber) {
      return this.bulkCost.logisticsProjectNumber;
    }
    const hasLineProjects = (this.bulkCost?.lines || []).some(line => !!line.logisticsProjectNumber || !!line.logisticsProjectId);
    return hasLineProjects ? fallbackForMultiple : 'Not assigned';
  }

  private projectLabel(project: LogisticsProjectSummary): string {
    const number = project.projectNumber || `Project #${project.projectId}`;
    return project.projectName ? `${number} - ${project.projectName}` : number;
  }

  private toDriverOptions(drivers: Driver[]): SelectItem<number>[] {
    const seen = new Set<number>();
    return drivers.filter(driver => !!driver.id && !seen.has(driver.id) && !!seen.add(driver.id)).map(driver => ({
      value: driver.id,
      label: driver.name || driver.legalName || `Driver #${driver.id}`
    }));
  }

  private toVehicleOptions(vehicles: Vehicle[]): SelectItem<number>[] {
    const seen = new Set<number>();
    return vehicles.filter(vehicle => !!vehicle.vehicleId && !seen.has(vehicle.vehicleId) && !!seen.add(vehicle.vehicleId)).map(vehicle => ({
      value: vehicle.vehicleId,
      label: vehicle.plateNumber || vehicle.registrationNumber || vehicle.chassisNumber || `Vehicle #${vehicle.vehicleId}`
    }));
  }

  private ensureSnapshotAssetOptions(): void {
    const drivers = new Map(this.driverOptions.map(option => [option.value, option]));
    const vehicles = new Map(this.vehicleOptions.map(option => [option.value, option]));
    (this.bulkCost.lines || []).forEach(line => {
      if (line.driverId && !drivers.has(line.driverId)) {
        drivers.set(line.driverId, { value: line.driverId, label: line.driverName || `Driver #${line.driverId}` });
      }
      if (line.vehicleId && !vehicles.has(line.vehicleId)) {
        vehicles.set(line.vehicleId, { value: line.vehicleId, label: line.vehicleLabel || `Vehicle #${line.vehicleId}` });
      }
    });
    this.driverOptions = Array.from(drivers.values());
    this.vehicleOptions = Array.from(vehicles.values());
  }

  private loadSisterOwnedAssetOptions(companyGroupId: number): void {
    const companyId = this.companyId();
    if (!companyId) return;
    forkJoin({
      drivers: this.driverService.findDriversByCompanyGroup(0, 500, companyGroupId, companyId),
      vehicles: this.vehicleService.findVehiclesByCompanyGroup(0, 500, companyGroupId, companyId)
    }).subscribe({
      next: result => this.mergeAssetOptions(result.drivers.items || [], result.vehicles.items || []),
      error: error => this.messageService.add({
        severity: 'warn',
        summary: 'Sister-company fleet unavailable',
        detail: error?.status === 403
          ? sisterCompanyFleetAccessMessage('both')
          : 'Unable to load sister-company drivers and vehicles for cost lines.'
      })
    });
  }

  private mergeAssetOptions(drivers: Driver[], vehicles: Vehicle[]): void {
    drivers.forEach(driver => this.driversById.set(driver.id, driver));
    vehicles.forEach(vehicle => this.vehiclesById.set(vehicle.vehicleId, vehicle));
    this.driverOptions = this.toDriverOptions(Array.from(this.driversById.values()));
    this.vehicleOptions = this.toVehicleOptions(Array.from(this.vehiclesById.values()));
    this.ensureSnapshotAssetOptions();
    this.syncPartyFromAssignedAssets();
  }

  private syncPartyFromAssignedAssets(): void {
    const currentCompanyId = this.companyId();
    if (!currentCompanyId) return;

    const resource = (this.bulkCost.lines || [])
      .flatMap(line => [
        line.vehicleId ? this.vehiclesById.get(line.vehicleId) : undefined,
        line.driverId ? this.driversById.get(line.driverId) : undefined
      ])
      .find(candidate => this.internalResourceCompanyId(candidate) !== undefined);
    const resourceCompanyId = this.internalResourceCompanyId(resource);
    if (!resourceCompanyId) return;

    this.selectedCrmClient = null;
    if (resourceCompanyId === currentCompanyId) {
      this.partyMode = 'OWN_COMPANY';
      this.selectedSisterCompanyId = null;
      this.applyCompanyParty(currentCompanyId, this.session.getCompany()?.name);
      return;
    }

    const sister = this.sisterCompanyOptions.find(option => option.value === resourceCompanyId);
    if (!sister) return;
    this.partyMode = 'SISTER_COMPANY';
    this.selectedSisterCompanyId = resourceCompanyId;
    this.applyCompanyParty(resourceCompanyId, sister.label);
  }

  private internalResourceCompanyId(resource?: Driver | Vehicle): number | undefined {
    if (!resource) return undefined;
    const ownership = (resource.resourceOwnershipType || '').toUpperCase();
    if (ownership && ownership !== 'OWNED') return undefined;
    const ownerId = resource.resourceCompanyId ?? this.companyId();
    if (!ownerId) return undefined;
    return ownerId === this.companyId() || this.sisterCompanyOptions.some(option => option.value === ownerId)
      ? ownerId
      : undefined;
  }

  private applyCompanyParty(companyId?: number, companyName?: string | null): void {
    this.bulkCost.supplierPartyId = companyId;
    this.bulkCost.supplierPartyType = companyId ? 'Company' : undefined;
    this.bulkCost.supplierName = companyName || (companyId ? `Company #${companyId}` : undefined);
  }

  private totalDirectAmount(): number {
    return (this.bulkCost.lines || []).reduce((sum, line) =>
      sum + Number(line.quantity || 0) * Number(line.price || 0), 0);
  }

  private percentageLineAmount(index: number): number {
    const lines = this.bulkCost.lines || [];
    const total = Number(this.bulkCost.allocationTotal || 0);
    if (!lines.length || total <= 0) return 0;
    if (index === lines.length - 1) {
      const preceding = lines.slice(0, index).reduce((sum, line) =>
        sum + total * Number(line.allocationPercentage || 0) / 100, 0);
      return total - preceding;
    }
    return total * Number(lines[index]?.allocationPercentage || 0) / 100;
  }

  private distributeEvenPercentages(): void {
    const lines = this.bulkCost.lines || [];
    if (!lines.length) return;
    const perLine = Math.floor((100 / lines.length) * 10000) / 10000;
    let assigned = 0;
    lines.forEach((line, index) => {
      line.allocationPercentage = index === lines.length - 1 ? 100 - assigned : perLine;
      assigned += Number(line.allocationPercentage || 0);
    });
  }

  private newBulkCost(): BulkCost {
    return {
      owningCompanyId: this.companyId(),
      sourceType: 'MANUAL',
      status: 'UNAPPROVED',
      approvalStatus: 'PENDING',
      postingStatus: 'NOT_POSTED',
      currencyCode: 'SAR',
      exchangeRate: 1,
      allocationMode: 'DIRECT',
      allocationTotal: 0,
      invoiceDate: new Date(),
      invoiceReceivedDate: new Date(),
      postingDate: new Date(),
      includeTax: false,
      lines: [this.newLine()]
    };
  }

  private newLine(): BulkCostLine {
    return {
      sourceType: this.bulkCost?.sourceType || 'MANUAL',
      description: 'Transportation',
      unit: 'Trip',
      quantity: 1,
      price: 0,
      allocationPercentage: 0,
      taxAmount: 0,
      amount: 0,
      status: 'UNAPPROVED'
    };
  }

  private applyAccountDefaults(sourceType: string): void {
    const defaults = this.accountDefaultsBySource.get(sourceType);
    if (!defaults?.active) return;
    if (this.bulkCost.payableAccountId == null && defaults.defaultCreditAccountId != null) {
      this.bulkCost.payableAccountId = defaults.defaultCreditAccountId;
    }
    if (defaults.defaultDebitAccountId != null) {
      (this.bulkCost.lines || []).filter(line => line.accountId == null)
        .forEach(line => line.accountId = defaults.defaultDebitAccountId);
    }
  }

  private prepareForEdit(cost: BulkCost): BulkCost {
    const lines = cost.lines?.length ? cost.lines : [this.newLine()];
    return {
      ...cost,
      allocationMode: cost.allocationMode === 'PERCENTAGE' ? 'PERCENTAGE' : 'DIRECT',
      allocationTotal: cost.allocationTotal ?? cost.grossAmount ?? 0,
      invoiceDate: this.toDate(cost.invoiceDate),
      invoiceReceivedDate: this.toDate(cost.invoiceReceivedDate),
      postingDate: this.toDate(cost.postingDate),
      lines: lines.map(line => ({
        ...line,
        allocationPercentage: line.allocationPercentage ?? 0,
        driverId: line.driverId ?? cost.driverId,
        driverName: line.driverName || cost.driverName,
        vehicleId: line.vehicleId ?? cost.vehicleId,
        vehicleLabel: line.vehicleLabel || cost.vehicleLabel
      }))
    };
  }

  private serialize(cost: BulkCost): BulkCost {
    return {
      ...cost,
      invoiceDate: this.apiDate(cost.invoiceDate),
      invoiceReceivedDate: this.apiDate(cost.invoiceReceivedDate),
      postingDate: this.apiDate(cost.postingDate),
      lines: (cost.lines || []).map((line, index) => ({
        ...line,
        amount: this.lineAmount(line, index),
        localAmount: this.lineTotal(line, index) * Number(cost.exchangeRate || 1)
      }))
    };
  }

  private companyId(): number | undefined {
    return this.session.getCompany()?.id;
  }

  private apiDate(value?: Date | string | null): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  private toDate(value?: Date | string | null): Date | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
}
